import type { ReactNode } from 'react'

import type { components } from '../../lib/api.types'
import { formatMinutes } from '../../lib/format'
import { Card, Pill, type PillTone } from '../ui'

type PlaybookEntry = components['schemas']['PlaybookEntry']

/** Closed contract keys select the board's pills; counts never select a label. */
const LABELS: Record<PlaybookEntry['label'], { words: string[]; tone: PillTone }> = {
  consistent_so_far_descriptive: { words: ['Consistent so far', 'Descriptive'], tone: 'green' },
  within_range: { words: ['Within range'], tone: 'neutral' },
  exploratory_limits_apply: { words: ['Exploratory', 'Limits apply'], tone: 'amber' },
  no_pattern_yet: { words: ['No pattern yet'], tone: 'neutral' },
}

const SHARE = new Intl.NumberFormat('en-GB', { style: 'percent', maximumFractionDigits: 1 })

/** The four parallel columns. The producer supplies every count and next step. */
export function PlaybookCard({ entry, children, onOpen }: {
  entry: PlaybookEntry
  children?: ReactNode
  onOpen?: (open: boolean) => void
}) {
  const label = LABELS[entry.label]
  const reported = entry.reported
  const control = entry.against_control
  return <Card as="section" className="playbook__card" aria-label={entry.activity.name}>
    <div className="playbook__columns">
      <div className="playbook__identity">
        <h2>{entry.activity.name}</h2>
        <p className="playbook__muted">{entry.runs_complete} {entry.runs_complete === 1 ? 'run' : 'runs'} · {entry.distinct_days} distinct {entry.distinct_days === 1 ? 'day' : 'days'}</p>
        <p className="playbook__muted">
          {entry.activity.duration_s !== null && <>{formatMinutes(entry.activity.duration_s)} · </>}
          Settle · {formatMinutes(entry.activity.settle_s)}
        </p>
        <div className="playbook__pills">
          {label.words.map((word, index) => <Pill key={word} tone={index === 0 ? label.tone : 'neutral'}>{word}</Pill>)}
          {entry.activity.control && <Pill>Control</Pill>}
          {Boolean(entry.runs_not_comparable) && <Pill tone="amber">{entry.runs_not_comparable} not comparable</Pill>}
          {Boolean(entry.runs_incomplete) && <Pill>{entry.runs_incomplete} incomplete</Pill>}
        </div>
      </div>
      <div className="playbook__column">
        <h3 className="playbook__label">Readings</h3>
        {Object.entries(entry.readings).map(([measure, counts]) => <div key={measure} className="playbook__measure">
          {/* Stage-1 keys have no display title: show the supplied key, never invent a measure name. */}
          <b className="playbook__measure-key">{measure}</b>
          <dl className="playbook__facts">
            <dt>Higher after</dt><dd>{counts.higher_after}</dd>
            <dt>Lower after</dt><dd>{counts.lower_after}</dd>
            <dt>Within</dt><dd>{counts.within}</dd>
            {counts.beyond_share != null && <><dt>Beyond your repeat variation</dt><dd>{SHARE.format(counts.beyond_share)}</dd></>}
          </dl>
        </div>)}
        {Boolean(entry.runs_not_comparable) && <p className="playbook__muted">{entry.runs_not_comparable} kept as not comparable, not as a zero.</p>}
        {Boolean(entry.runs_incomplete) && <p className="playbook__muted">{entry.runs_incomplete} kept as incomplete; nothing is compared.</p>}
      </div>
      <div className="playbook__column">
        <h3 className="playbook__label">Reported</h3>
        <dl className="playbook__facts">
          {'reported_calm_or_energized_after' in reported ? <>
            <dt>{reported.state_transition_label ?? 'reported_calm_or_energized_after'}</dt><dd>{reported.reported_calm_or_energized_after}</dd>
            <dt>To calm</dt><dd>{reported.to_calm}</dd>
            <dt>To energized</dt><dd>{reported.to_energized}</dd>
          </> : <>
            <dt>Calmer or more alert</dt><dd>{reported.calmer_or_more_alert}</dd>
            <dt>Calmer</dt><dd>{reported.calmer}</dd>
            <dt>More alert</dt><dd>{reported.more_alert}</dd>
          </>}
          <dt>Unchanged</dt><dd>{entry.reported.unchanged}</dd>
          <dt>Stress lower</dt><dd>{entry.reported.stress_lower}</dd>
          <dt>Stress unchanged</dt><dd>{entry.reported.stress_unchanged}</dd>
          <dt>Stress higher</dt><dd>{entry.reported.stress_higher}</dd>
        </dl>
      </div>
      <div className="playbook__column">
        <h3 className="playbook__label">{entry.activity.control ? 'Role' : entry.activity.exploratory ? 'What would help' : 'Against quiet rest'}</h3>
        {entry.against_control !== null && <dl className="playbook__facts">
          <dt>Quiet-rest days</dt><dd>{entry.against_control.distinct_days}</dd>
          <dt>Readings within range</dt><dd>{entry.against_control.readings_within}</dd>
          {control && ('reported_to_calm' in control
            ? <><dt>Reported to calm</dt><dd>{control.reported_to_calm}</dd></>
            : <><dt>Reported calmer</dt><dd>{control.reported_calmer}</dd></>)}
        </dl>}
        {entry.against_control === null && entry.control_days !== null && <p>{entry.control_days} quiet-rest days</p>}
        <p className="playbook__next">{entry.next}</p>
        {entry.activity.limit_text && <p className="playbook__muted">{entry.activity.limit_text}</p>}
      </div>
    </div>
    <details className="playbook__runs" onToggle={(event) => onOpen?.(event.currentTarget.open)}>
      <summary>Runs · {entry.activity.name}</summary>
      {children}
    </details>
  </Card>
}
